import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useAnnouncementBanner } from "@/hooks/useAnnouncementBanner";
import { useScrollHeader } from "@/hooks/useScrollHeader";
import { cn } from "@/lib/utils";

const DISMISSED_KEY = "announcement_banner_dismissed";

export function AnnouncementBanner() {
  const {
    enabled,
    content,
    bgColor,
    textColor,
    linkUrl,
    scrollSpeed,
    isLoading,
  } = useAnnouncementBanner();
  const { scrollDirection, isScrolled } = useScrollHeader();
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (dismissed === "true") {
      setIsDismissed(true);
    }
  }, []);

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  if (isLoading || !enabled || isDismissed || !content) {
    return null;
  }

  const isHiddenByScroll = scrollDirection === "down" && isScrolled;

  const bannerStyle = {
    backgroundColor: `hsl(${bgColor})`,
    color: `hsl(${textColor})`,
  };

  const BannerContent = () => (
    <div className="relative flex items-center h-10 overflow-hidden">
      {/* Scrolling text container */}
      <div
        className="animate-scroll-left"
        style={
          { "--scroll-duration": `${scrollSpeed}s` } as React.CSSProperties
        }
      >
        <span className="inline-flex items-center whitespace-nowrap">
          <span className="px-8 text-sm font-medium">{content}</span>
          <span className="px-4 text-sm opacity-60">•</span>
        </span>
        <span className="inline-flex items-center whitespace-nowrap">
          <span className="px-8 text-sm font-medium">{content}</span>
          <span className="px-4 text-sm opacity-60">•</span>
        </span>
      </div>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute right-2 p-1 rounded-full hover:bg-black/20 transition-colors z-10"
        aria-label="Dismiss announcement"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );

  if (linkUrl) {
    return (
      <a
        href={linkUrl}
        target={linkUrl.startsWith("http") ? "_blank" : undefined}
        rel={linkUrl.startsWith("http") ? "noopener noreferrer" : undefined}
        className={cn(
          "fixed top-0 left-0 right-0 z-[60] transition-transform duration-300",
          "hover:opacity-90",
          isHiddenByScroll ? "-translate-y-full" : "translate-y-0",
        )}
        style={bannerStyle}
      >
        <BannerContent />
      </a>
    );
  }

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[60] transition-transform duration-300",
        isHiddenByScroll ? "-translate-y-full" : "translate-y-0",
      )}
      style={bannerStyle}
    >
      <BannerContent />
    </div>
  );
}

// Export hook for other components to check banner visibility
export function useAnnouncementBannerVisible() {
  const { enabled, content, isLoading } = useAnnouncementBanner();
  const { scrollDirection, isScrolled } = useScrollHeader();
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (dismissed === "true") {
      setIsDismissed(true);
    }
  }, []);

  const isHiddenByScroll = scrollDirection === "down" && isScrolled;

  return (
    !isLoading && enabled && !isDismissed && !!content && !isHiddenByScroll
  );
}
